import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges
} from '@angular/core';
import { PortalTypeIdEnum } from '../../../../models/enum/shared/portal-type-id.enum';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { SpousalWaiverTypeEnum } from '../../../../models/spousal-waivers/spousal-waiver-type.enum';
import { SubmissionSpousalWaiver } from '../../../../models/submissions/submission-spousal-waiver';
import { ButtonTypeEnum } from '../../../../ui/buttons/button/button-type.enum';
import { map, startWith } from 'rxjs/operators';
import * as moment from 'moment';

export interface SpousalWaiverDatesUpdate {
  spousalWaiverId: string;
  signedDate: string;
  effectiveDate: string;
}

@Component({
  selector: 'app-ip-beneficiary-submission-spousal-waiver',
  templateUrl: './ip-beneficiary-submission-spousal-waiver.component.html',
  styleUrls: ['./ip-beneficiary-submission-spousal-waiver.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class IpBeneficiarySubmissionSpousalWaiverComponent implements OnChanges {
  @Input({ required: true }) spousalWaiver!: SubmissionSpousalWaiver;
  @Input({ required: true }) createdInPortalTypeId!: PortalTypeIdEnum;
  @Input({ required: true }) waiverTypeId!: SpousalWaiverTypeEnum;
  @Input() submissionStatusChanged = false;
  @Input() isLoading = false;
  @Output() saveDates = new EventEmitter<SpousalWaiverDatesUpdate>();

  public formSubmitted = false;

  public formGroup = new FormGroup({
    spousalWaiverDateSigned: new FormControl<string | null>(null, Validators.required),
    spousalWaiverEffectiveDate: new FormControl<string | null>(null, Validators.required)
  });

  // The CTA buttons only surface once the entered dates differ from what is currently persisted on the waiver.
  public hasPendingChanges$ = this.formGroup.valueChanges.pipe(
    startWith(null),
    map(() => this.hasPendingChanges())
  );

  public customErrorMessages = {
    spousalWaiverDateSigned: {
      required: $localize`Date signed is required`
    },
    spousalWaiverEffectiveDate: {
      required: $localize`Effective date is required`
    }
  };

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.spousalWaiver) {
      this.resetFormToWaiver();
    }
  }

  public cancel(): void {
    this.resetFormToWaiver();
  }

  public save(): void {
    this.formSubmitted = true;
    if (this.formGroup.invalid) {
      return;
    }
    this.saveDates.emit({
      spousalWaiverId: this.spousalWaiver.id,
      signedDate: this.toDateOnly(this.formGroup.controls.spousalWaiverDateSigned.value) ?? '',
      effectiveDate: this.toDateOnly(this.formGroup.controls.spousalWaiverEffectiveDate.value) ?? ''
    });
  }

  private resetFormToWaiver(): void {
    this.formSubmitted = false;
    this.formGroup.reset({
      spousalWaiverDateSigned: this.spousalWaiver.signedDate,
      spousalWaiverEffectiveDate: this.spousalWaiver.effectiveDate
    });
  }

  private hasPendingChanges(): boolean {
    return (
      this.toDateOnly(this.formGroup.controls.spousalWaiverDateSigned.value) !==
        this.toDateOnly(this.spousalWaiver.signedDate) ||
      this.toDateOnly(this.formGroup.controls.spousalWaiverEffectiveDate.value) !==
        this.toDateOnly(this.spousalWaiver.effectiveDate)
    );
  }

  private toDateOnly(value: string | null): string | null {
    return value ? moment.utc(value).format('YYYY-MM-DD') : null;
  }

  protected readonly PortalTypeIdEnum = PortalTypeIdEnum;
  protected readonly ButtonTypeEnum = ButtonTypeEnum;
}
